<template>
  <Dialog
    v-model:visible="visible"
    header="Type something to search"
    :modal="true"
    :style="{ maxWidth: '700px', width: '75vw' }"
    :closable="true"
    @hide="handleClose"
  >
    <div class="flex flex-col gap-4">
      <div class="relative">
        <div class="relative">
          <Icon
            name="lucide:search"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size="18"
          />
          <InputText
            v-model="searchQuery"
            placeholder="Search contacts and businesses..."
            class="w-full !px-10"
            autofocus
            @input="handleSearch"
            @keydown.escape="handleClose"
            @keydown.enter="handleEnterKey"
          />
          <div
            v-if="isLoading"
            class="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <ProgressSpinner
              style="width: 20px; height: 20px"
              stroke-width="4"
            />
          </div>
        </div>
      </div>

      <div
        v-if="error"
        class="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"
      >
        {{ error }}
      </div>

      <div
        v-if="searchQuery.trim().length >= 2"
        class="max-h-[500px] overflow-y-auto"
      >
        <div
          v-if="isLoading"
          class="flex flex-col items-center justify-center py-8"
        >
          <span class="ml-3 text-gray-500">Searching...</span>
        </div>

        <div
          v-else-if="!isLoading && searchResults.total === 0"
          class="text-center py-8 text-gray-500"
        >
          <Icon
            name="lucide:search-x"
            class="mx-auto mb-2 text-gray-400"
            size="48"
          />
          <p>No results found</p>
          <p class="text-sm mt-1">
            Try searching with different keywords
          </p>
        </div>

        <div v-else-if="searchResults.total > 0">
          <template v-if="searchResults.contacts.length > 0">
            <div class="mb-6">
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-sm font-semibold text-gray-700">
                  Contacts
                </h3>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  v-for="contact in searchResults.contacts"
                  :key="contact.id"
                  class="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm cursor-pointer transition-all"
                  @click="handleContactClick(contact)"
                >
                  <div class="flex-shrink-0">
                    <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <span class="text-sm font-medium text-gray-600">
                        {{ getContactInitials(contact) }}
                      </span>
                    </div>
                  </div>

                  <div class="flex-1 min-w-0">
                    <div class="font-semibold text-gray-900 text-sm mb-1">
                      {{ contact.displayName }}
                    </div>
                    <div
                      v-if="contact.email"
                      class="text-xs text-gray-600 mb-0.5 truncate"
                    >
                      <span v-if="contact.email.startsWith('cimpro.app+')" />
                      <span v-else>{{ contact.email }}</span>
                    </div>
                    <div
                      v-if="contact.phone && contact.phone !== '-'"
                      class="text-xs text-gray-600 truncate"
                    >
                      {{ contact.phone }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-if="searchResults.businesses.length > 0">
            <div>
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-sm font-semibold text-gray-700">
                  Businesses
                </h3>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  v-for="business in searchResults.businesses"
                  :key="business.id"
                  class="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm cursor-pointer transition-all"
                  @click="handleBusinessClick(business)"
                >
                  <div class="flex-shrink-0">
                    <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <Icon
                        name="hugeicons:building-01"
                        class="w-5 h-5 text-gray-500"
                      />
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="font-semibold text-gray-900 text-sm mb-2 line-clamp-2">
                      {{ business.displayName }}
                    </div>
                    <div class="flex flex-wrap gap-1.5">
                      <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        Business
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div
        v-else
        class="text-center py-8 text-gray-500"
      >
        <Icon
          name="lucide:search"
          class="mx-auto mb-2 text-gray-400"
          size="48"
        />
        <p>Start typing to search</p>
        <p class="text-sm mt-1">
          Search by name, email, phone, or business name
        </p>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUniversalSearch } from '~/composables/useUniversalSearch'
import type { SearchContact, SearchBusiness } from '~/composables/useUniversalSearch'

const router = useRouter()
const { searchResults, error, isLoading, search, clearResults } = useUniversalSearch()

const visible = ref(false)
const searchQuery = ref('')

const handleSearch = async () => {
  if (searchQuery.value.trim().length >= 2) {
    await search(searchQuery.value)
  }
  else {
    clearResults()
  }
}

const handleContactClick = (contact: SearchContact) => {
  router.push(`/contacts/${contact.id}/activity`)
  handleClose()
}

const handleBusinessClick = (business: SearchBusiness) => {
  router.push(`/businesses/${business.id}/details/profile`)
  handleClose()
}

const getContactInitials = (contact: SearchContact) => {
  const first = contact.firstName?.[0] || ''
  const last = contact.lastName?.[0] || ''
  return (first + last).toUpperCase() || '?'
}

const handleEnterKey = () => {
  // If there's exactly one result, navigate to it
  if (searchResults.value.total === 1) {
    if (searchResults.value.contacts.length === 1) {
      handleContactClick(searchResults.value.contacts[0])
    }
    else if (searchResults.value.businesses.length === 1) {
      handleBusinessClick(searchResults.value.businesses[0])
    }
  }
}

const handleClose = () => {
  visible.value = false
  searchQuery.value = ''
  clearResults()
}

watch(visible, (newValue) => {
  if (!newValue) {
    searchQuery.value = ''
    clearResults()
  }
})

function showDialog() {
  visible.value = true
}

defineExpose({
  showDialog,
})
</script>
